// Piezas básicas del recorrido: marca, etiqueta, botones, chips y tarjeta.
import React from 'react';
import { Box, Button, ButtonProps, Typography } from '@mui/material';
import { J, JourneyTone } from '../journeyTokens';

export const BrandMark: React.FC<{ href?: string }> = ({ href = '/' }) => (
  <Box component="a" href={href} sx={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none', color: J.text }}>
    <Box component="img" src="/logo.png" alt="PROA" sx={{ height: 30, width: 'auto', display: 'block' }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
      <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '.32em', color: J.text }}>PROA</Box>
      <Box component="span" sx={{ fontSize: 15, fontWeight: 800, letterSpacing: '.06em', color: J.amber, mt: '4px' }}>PRICING</Box>
    </Box>
  </Box>
);

export const Eyebrow: React.FC<{ children: React.ReactNode; tone?: 'amber' | 'sky'; both?: boolean }> = ({ children, tone = 'amber', both }) => {
  const hair = <Box component="span" sx={{ display: 'block', width: 32, height: '1px', bgcolor: tone === 'sky' ? 'rgba(56,189,248,.6)' : 'rgba(252,211,77,.55)' }} />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: tone === 'sky' ? J.skyText : J.body }}>
      {hair}{children}{both && hair}
    </Box>
  );
};

export type JourneyButtonVariant = 'primary' | 'amber' | 'ghost' | 'quiet';

const BUTTON_STYLES: Record<JourneyButtonVariant, object> = {
  primary: { bgcolor: J.sky, color: J.onSky, '&:hover': { bgcolor: '#7dd3fc' } },
  amber: { bgcolor: J.amber, color: J.onSky, '&:hover': { bgcolor: '#fde68a' } },
  ghost: { bgcolor: 'transparent', border: '1px solid #475569', color: '#e2e8f0', '&:hover': { bgcolor: 'rgba(148,163,184,.08)', borderColor: '#64748b' } },
  quiet: { bgcolor: 'transparent', color: J.skyText, px: '8px', '&:hover': { bgcolor: 'rgba(56,189,248,.08)' } },
};

export const JourneyButton: React.FC<Omit<ButtonProps, 'variant' | 'size'> & { kind?: JourneyButtonVariant; small?: boolean; block?: boolean }> = ({ kind = 'primary', small, block, sx, ...rest }) => (
  <Button
    disableElevation
    {...rest}
    sx={{
      minHeight: small ? 36 : 48,
      px: small ? '14px' : '22px',
      borderRadius: small ? '8px' : J.radiusControl,
      fontFamily: J.font,
      fontSize: small ? 13 : 15,
      fontWeight: 700,
      textTransform: 'none',
      whiteSpace: 'nowrap',
      width: block ? '100%' : undefined,
      border: '1px solid transparent',
      ...BUTTON_STYLES[kind],
      '&.Mui-disabled': { opacity: 0.55, color: kind === 'primary' || kind === 'amber' ? J.onSky : J.muted },
      ...(sx as object),
    }}
  />
);

export type ChipTone = JourneyTone | 'neutral' | 'sky' | 'amber';

const CHIP: Record<ChipTone, { bg: string; border: string; color: string }> = {
  sky: { bg: 'rgba(56,189,248,.1)', border: 'rgba(56,189,248,.35)', color: J.skyText },
  info: { bg: 'rgba(56,189,248,.1)', border: 'rgba(56,189,248,.35)', color: J.skyText },
  amber: { bg: 'rgba(252,211,77,.1)', border: 'rgba(252,211,77,.4)', color: J.amber },
  warn: { bg: 'rgba(252,211,77,.1)', border: 'rgba(252,211,77,.4)', color: J.amber },
  ok: { bg: 'rgba(74,222,128,.1)', border: 'rgba(74,222,128,.4)', color: J.okText },
  danger: { bg: 'rgba(248,113,113,.1)', border: 'rgba(248,113,113,.4)', color: J.dangerText },
  neutral: { bg: 'rgba(148,163,184,.08)', border: J.borderStrong, color: J.body },
};

export const JourneyChip: React.FC<{ tone?: ChipTone; children: React.ReactNode }> = ({ tone = 'neutral', children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 26, px: '10px', borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: '.02em', border: '1px solid', bgcolor: CHIP[tone].bg, borderColor: CHIP[tone].border, color: CHIP[tone].color, alignSelf: 'flex-start' }}>
    {children}
  </Box>
);

export const JourneyCard: React.FC<{ children: React.ReactNode; tight?: boolean; sx?: object }> = ({ children, tight, sx }) => (
  <Box sx={{ bgcolor: J.card, border: `1px solid ${J.border}`, borderRadius: J.radiusCard, p: tight ? '20px' : '28px', ...sx }}>{children}</Box>
);

export const JourneyText: React.FC<{ variant: 'hero' | 'title' | 'h2' | 'h3' | 'lead' | 'body' | 'small'; children: React.ReactNode; sx?: object; component?: React.ElementType }> = ({ variant, children, sx, component }) => {
  const styles: Record<string, object> = {
    hero: { fontSize: 40, fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.08, color: J.text },
    title: { fontSize: 30, fontWeight: 800, letterSpacing: '-.025em', lineHeight: 1.15, color: J.text },
    h2: { fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: J.text },
    h3: { fontSize: 16, fontWeight: 700, color: J.text },
    lead: { fontSize: 16, lineHeight: 1.6, color: J.muted },
    body: { fontSize: 14, lineHeight: 1.6, color: J.body },
    small: { fontSize: 13, lineHeight: 1.5, color: J.muted },
  };
  const defaultComponent = variant === 'title' || variant === 'hero' ? 'h1' : variant === 'h2' ? 'h2' : variant === 'h3' ? 'h3' : 'p';
  return (
    <Typography component={component ?? defaultComponent} sx={{ fontFamily: J.font, m: 0, ...styles[variant], ...sx }}>
      {children}
    </Typography>
  );
};
